// force-rebuild-nonce 2026-07-07T21:12Z — Vercel bundle stuck; touching
// a real source file so the build hash changes for sure.
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DateTime } from 'luxon';
import {
  ServicesInspectorClientService,
  ServiceSummary,
  ServiceDetail,
  LogFileEntry,
  TailPage,
  CronRow,
} from '../../@services/services-inspector.service';
import { CustomerDirectoryService } from '../../@services/customer-directory.service';

const TAIL_LINES = 100;

// Commerce integrations the Customer doc supports — kept in sync with
// the COMMERCE_INTEGRATIONS list in customer-directory.component.ts.
// `ready: false` rows render as Coming Soon and are non-clickable.
const COMMERCE_INTEGRATIONS: { key: 'woocommerce' | 'kartra' | 'stripe' | 'samcart'; label: string; ready: boolean }[] = [
  { key: 'woocommerce', label: 'WooCommerce', ready: true },
  { key: 'kartra',      label: 'Kartra',      ready: true },
  { key: 'samcart',     label: 'SamCart',     ready: true },
  { key: 'stripe',      label: 'Stripe',      ready: false },
];

export interface CommerceConnectionRow {
  customer_id: string;
  customer_name: string;
  integration: 'woocommerce' | 'kartra' | 'stripe' | 'samcart';
  integration_label: string;
  enabled: boolean;
  default_scanner: string;
  product_count: number;
}

@Component({
  standalone: true,
  selector: 'app-services',
  imports: [CommonModule, FormsModule],
  templateUrl: './services.component.html',
  styleUrl: './services.component.scss',
})
export class ServicesComponent implements OnInit {

  // View mode — 'services' (pm2 process list + detail, original UX) OR
  // 'crons' (fleet-wide node-cron registry + last/next fire). Mike
  // 2026-07-04: routes both under the same admin/services URL so ops
  // can flip contexts without breaking the Services deep-links
  // captured in old ERs.
  viewMode: 'services' | 'crons' = 'services';

  // Box filter — null = all boxes. Shared across both view modes so
  // clicking a box narrows both the pm2 list AND the cron rows.
  boxes: string[] = [];
  localBox = '';
  selectedBox: string | null = null;

  // Services-mode list
  services: ServiceSummary[] = [];
  filterText = '';

  // Crons-mode list
  crons: CronRow[] = [];
  cronsFilterText = '';
  cronsAutoRefreshHandle: any = null;

  // Detail
  detail: ServiceDetail | null = null;
  detailTab: 'settings' | 'logs' = 'settings';

  // Logs tab state
  selectedFile: LogFileEntry | null = null;
  tail: TailPage | null = null;
  // Stack of `before` values to walk newer pages back. Each entry is the
  // `before` we used to fetch a page; popping returns to the page above it.
  pageStack: (number | undefined)[] = [];
  loadingTail = false;
  clearing = false;
  // Transient — swaps the Copy button to a green check + "Copied" label
  // for ~1.5s after a successful clipboard write, then reverts. Purely
  // cosmetic feedback so ops sees the copy landed.
  copiedTail = false;
  // IT-F2-215 diag: state for the "Copy Context" toolbar button (metadata
  // + currently-loaded tail, wrapped in a markdown block Mike can paste
  // into a chat with an agent for triage without stripping context).
  copiedContext = false;
  private _copiedContextTimer: any = null;
  private _copiedResetTimer: any = null;
  // Per-line copy feedback (log tail viewer). Set to the line index
  // that was just copied; drives the fa-copy → fa-check swap on that
  // specific line's Copy pill. Reverts after 1.5s.
  copiedLineIdx: number | null = null;
  private _copiedLineTimer: any = null;

  // Commerce Connections section
  commerceRows: CommerceConnectionRow[] = [];
  commerceLoading = false;
  commerceFilter = '';
  commerceShowDisabled = false;

  constructor(
    private svc: ServicesInspectorClientService,
    private customerDirSvc: CustomerDirectoryService,
    private router: Router,
    private _sanitizer: DomSanitizer,
  ) {}

  async ngOnInit(): Promise<void> {
    const { boxes, local } = await this.svc.listBoxes();
    this.boxes = boxes;
    this.localBox = local;
    await this.loadList();
    // Fire-and-forget — commerce list is independent of pm2 services
    // and shouldn't block the panel painting.
    this.loadCommerceConnections();
    // Restore last-opened service + file from localStorage so a browser
    // refresh puts the operator back where they were. Runs after the
    // service list is loaded so the target service exists in memory.
    await this._restorePersistedSelection();
  }

  // localStorage keys — namespaced to this panel so a stray key doesn't
  // clash with other components. Mike 2026-07-07.
  private static readonly LS_KEY_SERVICE = 'services.lastOpened';
  private static readonly LS_KEY_FILE    = 'services.lastFile';

  private async _restorePersistedSelection(): Promise<void> {
    try {
      const raw = localStorage.getItem(ServicesComponent.LS_KEY_SERVICE);
      if (!raw) return;
      const { box, name } = JSON.parse(raw);
      if (!box || !name) return;
      const match = this.services.find(s => s.box === box && s.name === name);
      if (!match) return;
      await this.openDetail(match);
      const lastFile = localStorage.getItem(ServicesComponent.LS_KEY_FILE);
      if (lastFile && this.detail) {
        const f = this.detail.log_files.find(x => x.file === lastFile);
        if (f) await this.openFile(f);
      }
    } catch { /* corrupted LS entry — ignore */ }
  }

  private _persistService(box: string, name: string): void {
    try { localStorage.setItem(ServicesComponent.LS_KEY_SERVICE, JSON.stringify({ box, name })); }
    catch { /* quota exceeded / disabled localStorage */ }
  }
  private _persistFile(file: string): void {
    try { localStorage.setItem(ServicesComponent.LS_KEY_FILE, file); }
    catch { /* ignore */ }
  }

  // Fleet box → IP mapping for the tooltip on the Box filter buttons.
  // Keyed by SERVICES_BOX_NAME. Update when a new box joins the peer
  // list. Sourced from ~/.ssh/config aliases on 2026-07-07 (verified
  // by grep against ssh config). phoenix5 not in ssh config here so
  // it falls through to just the name if it appears. Mike 2026-07-07:
  // 'when i over over the server box at the top it should give me a
  // ip address of that box'.
  private static readonly BOX_IPS: Record<string, string> = {
    'web-backend-srvr-1': '131.153.202.158',
    'ai-srvr':            '131.153.224.203',
    'trade-desk':         '131.153.11.233',
    'ws':                 '131.153.231.201',
  };
  boxTitle(box: string): string {
    if (!box) return '';
    const ip = ServicesComponent.BOX_IPS[box] || '';
    return ip ? `${box} — ${ip}` : box;
  }

  // -------- list --------

  async loadList(): Promise<void> {
    this.services = await this.svc.list(this.selectedBox || undefined);
  }

  selectBoxFilter(box: string | null): void {
    this.selectedBox = box;
    this.loadList();
  }

  filteredServices(): ServiceSummary[] {
    const q = this.filterText.trim().toLowerCase();
    if (!q) return this.services;
    return this.services.filter(s =>
      s.name.toLowerCase().includes(q) ||
      (s.box || '').toLowerCase().includes(q)
    );
  }

  // -------- view mode --------

  /** Switch between Services / Crons view. First switch into 'crons'
   *  triggers a lazy fetch; subsequent switches reuse the cached list.
   */
  switchView(mode: 'services' | 'crons'): void {
    this.viewMode = mode;
    if (mode === 'crons' && this.crons.length === 0) {
      void this.loadCrons();
    }
  }

  // -------- crons --------

  async loadCrons(): Promise<void> {
    this.crons = await this.svc.listCrons();
  }

  /**
   * Same "match on name OR box" filter as the services list. Also
   * matches on cron_id + description so ops can grep by purpose
   * ('redis_flush', 'algo alerts', etc.) not just service name.
   */
  filteredCrons(): CronRow[] {
    let rows = this.crons;
    if (this.selectedBox) rows = rows.filter(r => r.box === this.selectedBox);
    const q = this.cronsFilterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.service.toLowerCase().includes(q) ||
      r.box.toLowerCase().includes(q) ||
      r.cron_id.toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q) ||
      (r.cron_expr || '').toLowerCase().includes(q)
    );
  }

  /**
   * Human-readable summary of a cron expression. Best-effort — well-known
   * common patterns we use in this fleet map to phrases; anything else
   * falls through to the raw expression. Keeps the panel readable without
   * pulling in cronstrue as a dep.
   */
  humanFreq(expr: string, tz: string | null): string {
    const raw = String(expr || '').trim();
    if (!raw) return '';
    const tzHint = tz ? ` (${tz})` : ' (UTC)';
    // Curated table for the schedules the fleet actually uses. Ordered
    // longest-first so 'M-F 8am-8pm every min' doesn't get eaten by
    // '* * * * *'.
    const KNOWN: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
      [/^\* 8-20 \* \* 1-5$/, () => `every minute, M-F 08:00–20:59${tzHint}`],
      [/^15,45 9-16 \* \* 1-5$/, () => `M-F at 09:15/45–16:15/45${tzHint}`],
      [/^0 (\d{1,2}) \* \* \*$/, (m) => `daily at ${m[1].padStart(2, '0')}:00${tzHint}`],
      [/^(\d{1,2}) (\d{1,2}) \* \* \*$/, (m) => `daily at ${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')}${tzHint}`],
      [/^(\d{1,2}) (\d{1,2}) \* \* 1-5$/, (m) => `M-F at ${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')}${tzHint}`],
      [/^(\d{1,2}) (\d{1,2}) \* \* (\d)$/, (m) => {
        const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(m[3]) % 7];
        return `${day} at ${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')}${tzHint}`;
      }],
      [/^0 (\d{1,2}) \* \* 1-5$/, (m) => `M-F at ${m[1].padStart(2, '0')}:00${tzHint}`],
      [/^0 0 \* \* 1-5$/, () => `M-F at 00:00${tzHint}`],
      [/^\*\/(\d+) (\d+)-(\d+) \* \* 1-5$/, (m) => `every ${m[1]}min, M-F ${m[2].padStart(2, '0')}:00–${m[3]}:59${tzHint}`],
      [/^(\d+)-(\d+)\/(\d+) (\d+) \* \* 1-5$/, (m) => `every ${m[3]}min, M-F ${m[4].padStart(2, '0')}:${m[1]}–${m[4].padStart(2, '0')}:${m[2]}${tzHint}`],
    ];
    for (const [pat, fn] of KNOWN) {
      const m = raw.match(pat);
      if (m) return fn(m);
    }
    return raw;
  }

  /**
   * Status of the last run: 'ok' (green), 'error' (red), 'running'
   * (yellow — started_at present but finished_at absent), or 'never'
   * (grey — no run recorded yet).
   */
  lastRunStatus(row: CronRow): 'ok' | 'error' | 'running' | 'never' {
    const r = row.last_run;
    if (!r) return 'never';
    if (r.finished_at == null) return 'running';
    return r.ok ? 'ok' : 'error';
  }

  formatDuration(ms: number | null | undefined): string {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
    if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)}h`;
    // IT-F2-215: services panel shows uptime for services running 30+
    // days at a time; without the day branch a 13-day uptime rendered
    // as "18688.2m" which is illegible + wrapped the secondary line to
    // 3 rows, crushing the name column.
    return `${(ms / 86_400_000).toFixed(ms < 30 * 86_400_000 ? 1 : 0)}d`;
  }

  // -------- detail --------

  async openDetail(svc: ServiceSummary): Promise<void> {
    this.detail = await this.svc.detail(svc.box, svc.name);
    this.detailTab = 'settings';
    this.selectedFile = null;
    this.tail = null;
    this.pageStack = [];
    this._persistService(svc.box, svc.name);
    // Clear file persistence when switching services so a stale file
    // name from a different service doesn't get re-attempted.
    try { localStorage.removeItem(ServicesComponent.LS_KEY_FILE); } catch {}
  }

  closeDetail(): void {
    this.detail = null;
    this.selectedFile = null;
    this.tail = null;
    this.pageStack = [];
    // Explicit close = intentional dismissal — drop persistence so the
    // next visit doesn't auto-restore what the operator just closed.
    try {
      localStorage.removeItem(ServicesComponent.LS_KEY_SERVICE);
      localStorage.removeItem(ServicesComponent.LS_KEY_FILE);
    } catch {}
  }

  // -------- logs tab --------

  async openLogTab(): Promise<void> {
    this.detailTab = 'logs';
    if (!this.detail || this.selectedFile) return;
    // Auto-select most recent active (non-rotated) file: prefer combined > out > error
    const active = this.detail.log_files.find(f => f.kind === 'combined')
                || this.detail.log_files.find(f => f.kind === 'out')
                || this.detail.log_files.find(f => f.kind === 'error')
                || this.detail.log_files[0];
    if (active) await this.openFile(active);
  }

  // Log tail viewer state — search box + last-opened file.
  //
  // Search: live-highlight matches in the currently-visible tail page
  // via <mark> tags. Case-insensitive substring. Doesn't filter lines
  // out — the operator sees the full paged window with highlights,
  // matching Chrome DevTools + tail-tree UX.
  tailSearch: string = '';

  async openFile(file: LogFileEntry): Promise<void> {
    if (!this.detail) return;
    this.selectedFile = file;
    this.pageStack = [];
    this.loadingTail = true;
    this.tail = await this.svc.tail(this.detail.box, this.detail.name, file.file, TAIL_LINES);
    this.loadingTail = false;
    this._persistFile(file.file);
  }

  // Render one tail line as SafeHtml — escapes HTML so the log content
  // can't inject markup, then wraps case-insensitive matches of
  // `tailSearch` in a <mark> tag. Empty search returns the plain
  // (escaped) line. Called from the template via [innerHTML].
  highlightedLine(line: string): SafeHtml {
    const escaped = this._escapeHtml(String(line ?? ''));
    const q = (this.tailSearch || '').trim();
    if (!q) return this._sanitizer.bypassSecurityTrustHtml(escaped);
    const qEsc = this._escapeHtml(q);
    // Regex escape the escaped-HTML search term so meta chars are
    // treated as literals.
    const rx = new RegExp(qEsc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const html = escaped.replace(rx, (m) => `<mark class="bg-yellow-300 text-black px-0.5 rounded-sm">${m}</mark>`);
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }
  private _escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async pageOlder(): Promise<void> {
    if (!this.detail || !this.selectedFile || !this.tail || this.tail.nextBefore == null) return;
    const before = this.tail.nextBefore;
    this.pageStack.push(before);
    this.loadingTail = true;
    this.tail = await this.svc.tail(this.detail.box, this.detail.name, this.selectedFile.file, TAIL_LINES, before);
    this.loadingTail = false;
  }

  async pageNewer(): Promise<void> {
    if (!this.detail || !this.selectedFile) return;
    // Drop the current page from the stack; pop the one before it to refetch.
    this.pageStack.pop();
    const before = this.pageStack.length ? this.pageStack[this.pageStack.length - 1] : undefined;
    this.loadingTail = true;
    this.tail = await this.svc.tail(this.detail.box, this.detail.name, this.selectedFile.file, TAIL_LINES, before);
    this.loadingTail = false;
  }

  canPageOlder(): boolean {
    return !!(this.tail && this.tail.nextBefore != null);
  }

  canPageNewer(): boolean {
    return this.pageStack.length > 0;
  }

  async refreshTail(): Promise<void> {
    if (!this.detail || !this.selectedFile) return;
    this.loadingTail = true;
    const before = this.pageStack.length ? this.pageStack[this.pageStack.length - 1] : undefined;
    this.tail = await this.svc.tail(this.detail.box, this.detail.name, this.selectedFile.file, TAIL_LINES, before);
    this.loadingTail = false;
  }

  // Jump to EOF (live tail). Clears any page-back cursor + refetches
  // the most-recent N lines. Semantically same as Reload from the
  // active tail — Newest is the intent-communicating variant that
  // returns from an Older-walked or Oldest-jumped view.
  async jumpNewest(): Promise<void> {
    if (!this.detail || !this.selectedFile) return;
    this.loadingTail = true;
    this.pageStack = [];
    this.tail = await this.svc.tail(this.detail.box, this.detail.name, this.selectedFile.file, TAIL_LINES);
    this.loadingTail = false;
  }

  // Jump to BOF (first N lines of the file). One-shot view — Older
  // paging is disabled after this because nextBefore is null; Newer
  // paging is disabled because pageStack is empty. Operator returns
  // to live via the Newest button.
  async jumpOldest(): Promise<void> {
    if (!this.detail || !this.selectedFile) return;
    this.loadingTail = true;
    const res = await this.svc.head(this.detail.box, this.detail.name, this.selectedFile.file, TAIL_LINES);
    if (res) {
      this.tail = { lines: res.lines, nextBefore: null, size: res.size } as TailPage;
      this.pageStack = [];
    }
    this.loadingTail = false;
  }

  download(file: LogFileEntry): void {
    if (!this.detail) return;
    // Use a transient anchor + withCredentials cookie. Modern browsers
    // honor cookies on top-level navigation to a same-eTLD endpoint.
    const url = this.svc.downloadUrl(this.detail.box, this.detail.name, file.file);
    window.open(url, '_blank');
  }

  // Copy a single log line to the clipboard. Wired to both the on-
  // hover Copy pill (left-click) and the right-click contextmenu on
  // the line div — both call this. `idx` drives the fa-check flash
  // so the specific line the operator interacted with confirms.
  copyLine(line: string, idx: number): void {
    if (line == null) return;
    const text = String(line);
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch { /* clipboard write can fail on locked-down browsers */ }
    this.copiedLineIdx = idx;
    if (this._copiedLineTimer) clearTimeout(this._copiedLineTimer);
    this._copiedLineTimer = setTimeout(() => {
      this.copiedLineIdx = null;
      this._copiedLineTimer = null;
    }, 1500);
  }

  // Right-click on a log line → copy that line directly. Blocks the
  // native browser context menu (which would only copy selected text,
  // not the whole line) so the intent-communicating shortcut wins.
  onLineContext(evt: MouseEvent, line: string, idx: number): void {
    if (!evt) return;
    evt.preventDefault();
    this.copyLine(line, idx);
  }

  // Copy the CURRENT tail page's lines to the clipboard as newline-
  // joined plain text. Only what's visible in the viewer — not the full
  // file. Ops workflow: page/scroll to the window of interest, hit
  // Copy, paste into an incident report. Confirmation is a 1.5s badge
  // flip on the button (fa-copy → fa-check + 'Copied').
  async copyTail(): Promise<void> {
    if (!this.tail?.lines?.length) return;
    const text = this.tail.lines.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback for insecure contexts or old browsers. Not
        // reached on admin.f2-tech.ai (https) but keeps the button
        // useful on file:// dev builds and any HTTP proxy scenario.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copiedTail = true;
      if (this._copiedResetTimer) clearTimeout(this._copiedResetTimer);
      this._copiedResetTimer = setTimeout(() => {
        this.copiedTail = false;
        this._copiedResetTimer = null;
      }, 1500);
    } catch {
      /* clipboard write can fail on locked-down browser policies;
         swallow so we don't surface a confusing error toast for what
         the operator can still work around by manual selection. */
    }
  }

  /**
   * IT-F2-215: build a markdown "context block" of the currently-open
   * service + log file + loaded tail, and copy to clipboard. Mike pastes
   * this into a chat with an agent so the agent knows exactly which
   * service, box, pm_id, log file, and log excerpt he's referring to
   * without playing 20 questions. Includes: service identity, pm2 runtime
   * (status/uptime/restarts/cpu/mem), git HEAD, script + cwd, log file
   * metadata, and the last N tail lines currently rendered.
   */
  async copyContext(): Promise<void> {
    if (!this.detail || !this.selectedFile) return;
    const d = this.detail;
    const f = this.selectedFile;
    const lines: string[] = [];
    lines.push('## Service context — from admin.f2-tech.ai services panel');
    lines.push('');
    lines.push(`- **Service**: \`${d.name}\` @ \`${d.box}\``);
    const rt: string[] = [];
    if (d.pm_id !== undefined) rt.push(`pm_id ${d.pm_id}`);
    if ((d as any).status) rt.push(`status ${(d as any).status}`);
    if ((d as any).uptime_ms !== undefined) rt.push(`up ${this.formatDuration((d as any).uptime_ms)}`);
    if ((d as any).restart_count !== undefined) rt.push(`↻${(d as any).restart_count}`);
    if ((d as any).unstable_restarts) rt.push(`unstable ${(d as any).unstable_restarts}`);
    if (rt.length) lines.push(`- **Runtime**: ${rt.join(' · ')}`);
    const load: string[] = [];
    if ((d as any).cpu_percent !== undefined) load.push(`CPU ${(d as any).cpu_percent}%`);
    if ((d as any).memory_bytes !== undefined) load.push(`Mem ${this.formatBytes((d as any).memory_bytes)}`);
    if (load.length) lines.push(`- **Load**: ${load.join(' · ')}`);
    if (d.exec_mode) lines.push(`- **Mode**: ${d.exec_mode} × ${d.instances ?? 1}${d.port ? ` · port ${d.port}` : ''}`);
    if (d.script) lines.push(`- **Script**: \`${d.script}\``);
    if (d.cwd) lines.push(`- **cwd**: \`${d.cwd}\``);
    if (d.git?.head) lines.push(`- **Git HEAD**: \`${d.git.head.slice(0, 12)}\`${d.git.branch ? ` (${d.git.branch})` : ''}`);
    lines.push(`- **Log file**: \`${f.file}\` (${f.kind}) · ${this.formatBytes(f.size_bytes)} · last modified ${this.formatTime(f.mtime)}`);
    if (this.tail?.size !== undefined) lines.push(`- **Tail window**: ${this.tail.lines?.length ?? 0} lines · file size at read ${this.formatBytes(this.tail.size)}`);
    lines.push('');
    if (this.tail?.lines?.length) {
      lines.push('### Tail (currently loaded)');
      lines.push('```');
      lines.push(...this.tail.lines);
      lines.push('```');
    } else {
      lines.push('_No tail loaded — file selected but no lines fetched yet._');
    }
    const text = lines.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copiedContext = true;
      if (this._copiedContextTimer) clearTimeout(this._copiedContextTimer);
      this._copiedContextTimer = setTimeout(() => {
        this.copiedContext = false;
        this._copiedContextTimer = null;
      }, 1800);
    } catch { /* clipboard write failed — swallow */ }
  }

  async clearFile(file: LogFileEntry): Promise<void> {
    if (!this.detail) return;
    const ok = window.confirm(
      `Truncate "${file.file}" on ${this.detail.box}?\n\n` +
      `This will set the file size to 0 in place. The owning process keeps writing — ` +
      `no restart is triggered. ${this.formatBytes(file.size_bytes)} of log content will be discarded.`
    );
    if (!ok) return;
    this.clearing = true;
    const res = await this.svc.clear(this.detail.box, this.detail.name, file.file);
    this.clearing = false;
    if (res?.ok) {
      // Refresh detail + tail so size updates.
      this.detail = await this.svc.detail(this.detail.box, this.detail.name);
      const fresh = this.detail?.log_files.find(f => f.file === file.file) || null;
      this.selectedFile = fresh;
      if (fresh) {
        this.pageStack = [];
        await this.openFile(fresh);
      } else {
        this.tail = null;
      }
    }
  }

  // -------- formatters --------

  formatBytes(n: number): string {
    if (!n && n !== 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  formatTime(iso?: string): string {
    if (!iso) return '—';
    try {
      const dt = DateTime.fromISO(iso).setZone('America/New_York');
      return dt.isValid ? dt.toFormat('LLL d, HH:mm:ss') + ' ET' : iso;
    } catch { return iso; }
  }

  envEntries(): { key: string; value: string }[] {
    if (!this.detail?.env_masked) return [];
    return Object.entries(this.detail.env_masked)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  remoteEntries(): { key: string; value: string }[] {
    if (!this.detail?.git?.remotes) return [];
    return Object.entries(this.detail.git.remotes).map(([key, value]) => ({ key, value: value as string }));
  }

  // -------- commerce connections --------

  async loadCommerceConnections(): Promise<void> {
    this.commerceLoading = true;
    try {
      const customers = await this.customerDirSvc.list();
      const rows: CommerceConnectionRow[] = [];
      for (const c of customers) {
        for (const def of COMMERCE_INTEGRATIONS) {
          const cfg = c?.[def.key];
          if (!cfg) continue;
          rows.push({
            customer_id: c._id,
            customer_name: c.name || c._id,
            integration: def.key,
            integration_label: def.label,
            enabled: !!cfg.enabled,
            default_scanner: cfg.default_scanner || '',
            product_count: Object.keys(cfg.product_map || {}).length,
          });
        }
      }
      // Stable sort: enabled first, then customer name, then integration label.
      rows.sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        const n = a.customer_name.localeCompare(b.customer_name);
        if (n !== 0) return n;
        return a.integration_label.localeCompare(b.integration_label);
      });
      this.commerceRows = rows;
    } finally {
      this.commerceLoading = false;
    }
  }

  filteredCommerceRows(): CommerceConnectionRow[] {
    const q = this.commerceFilter.trim().toLowerCase();
    return this.commerceRows.filter(r => {
      if (!this.commerceShowDisabled && !r.enabled) return false;
      if (!q) return true;
      return r.customer_name.toLowerCase().includes(q)
          || r.customer_id.toLowerCase().includes(q)
          || r.integration_label.toLowerCase().includes(q)
          || (r.default_scanner || '').toLowerCase().includes(q);
    });
  }

  commerceEnabledCount(): number {
    return this.commerceRows.filter(r => r.enabled).length;
  }

  // Deep-link into customer-directory's edit modal at the commerce
  // section. customer-directory.component reads the ?commerce= query
  // param after its list loads and triggers openCommerceSettings.
  openCommerceSettings(row: CommerceConnectionRow): void {
    this.router.navigate(['/admin/customer-directory'], {
      queryParams: { commerce: row.customer_id },
    });
  }
}
